'use client'

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { trackPostHogEvent } from '@/lib/utils/dataLayer'
import Footer from '@/components/Footer'
import WhitelabelCTA from '@/components/services/WhitelabelCTA'

/** One row in the resources index. */
export type StackPost = {
  slug: string
  title: string
  url: string
  category: string | null
  excerpt: string | null
  image: string | null
}

export type SmartStackClientProps = {
  posts: StackPost[]
  children?: ReactNode
}

type StackCtx = {
  posts: StackPost[]
}

const SmartStackContext = createContext<StackCtx | null>(null)

export function useSmartStack() {
  const ctx = useContext(SmartStackContext)
  if (!ctx) throw new Error('useSmartStack must be used inside SmartStackShell')
  return ctx
}

const STRIPE = 'repeating-linear-gradient(45deg,#dbe6f5 0 5px,#c3d5ec 5px 10px)'
const STRIPE_MUTE = 'repeating-linear-gradient(45deg,#eef1f5 0 5px,#e0e5ec 5px 10px)'

/** Featured image when the post has one, striped placeholder otherwise. */
function Thumb({
  src,
  className,
  mute = false,
}: {
  src: string | null
  className: string
  mute?: boolean
}) {
  const [failed, setFailed] = useState(false)
  if (src && !failed) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={src}
        alt=""
        loading="lazy"
        className={`${className} shrink-0 object-cover`}
        onError={() => setFailed(true)}
      />
    )
  }
  return (
    <div
      className={`${className} shrink-0`}
      style={{ backgroundImage: mute ? STRIPE_MUTE : STRIPE }}
    />
  )
}

export default function SmartStackShell({ posts, children }: SmartStackClientProps) {
  const pathname = usePathname()
  const router = useRouter()
  const [listOpen, setListOpen] = useState(false)
  const [category, setCategory] = useState<string | null>(null)

  const categories = useMemo(
    () =>
      [...new Set(posts.map((post) => post.category).filter((name): name is string => Boolean(name)))].sort(
        (a, b) => a.localeCompare(b),
      ),
    [posts],
  )

  const visiblePosts = category ? posts.filter((post) => post.category === category) : posts

  const selectCategory = (name: string | null) => {
    setCategory(name)
    setListOpen(false)
    if (pathname && pathname !== '/resources') router.push('/resources')
  }

  if (pathname === '/resources/free-seo-checklist') {
    return <>{children}</>
  }

  return (
    <SmartStackContext.Provider value={{ posts: visiblePosts }}>
    <div className="flex min-h-[calc(100dvh-80px)] flex-col bg-white text-slate-900">
      {/* ── Sidebar: full viewport on lg, covers the site header ─────────── */}
      <aside className="relative bg-[#0a2b5c] text-white lg:fixed lg:inset-y-0 lg:left-0 lg:z-[103] lg:flex lg:h-dvh lg:w-[400px] lg:flex-col lg:overflow-y-auto lg:overscroll-contain lg:[scrollbar-width:thin]">
        <div className="relative flex min-h-full w-full shrink-0 flex-col">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 overflow-hidden"
        >
          <img
            src="/images/resources/sirebar_stack.avif"
            alt=""
            className="h-full w-full object-cover object-[center_30%]"
          />
          <div className="absolute inset-0 bg-[#0a2b5c]/80" />
        </div>
        <div className="relative z-10 flex h-20 shrink-0 items-center px-6 sm:px-10">
          <Link
            href="/"
            className="flex items-center"
            aria-label="SmartMetrics"
            onClick={() => {
              trackPostHogEvent('menu_navigation', {
                menu_item: 'Logo',
                location: 'smart_stack_sidebar',
                pathname: pathname || '/',
              })
            }}
          >
            <Image
              src="/images/logo_white.png"
              alt="SmartMetrics - Marketing and AI services"
              width={147}
              height={40}
              className="h-[30px] w-[147px] object-contain"
              priority
              sizes="147px"
            />
          </Link>
        </div>

        <div className="relative z-10 px-6 pb-10 pt-2 sm:px-10 lg:flex-1">
        <p className="text-[14.5px] leading-[1.5] text-blue-100/85">
          The SmartMetrics resources index: tools and services we actually use, skip, and watch in analytics, CRM, and AI search.
        </p>

        <SubscribeForm />

        <button
          type="button"
          onClick={() => setListOpen((open) => !open)}
          aria-expanded={listOpen}
          aria-controls="resource-categories"
          className="mt-6 flex w-full items-center justify-between border-t border-white/15 pt-5 text-[13px] font-bold uppercase tracking-[0.12em] text-sky-300 lg:hidden"
        >
          Categories
          <span aria-hidden="true">{listOpen ? '–' : '+'}</span>
        </button>

        <nav
          id="resource-categories"
          aria-label="Categories"
          className={`mt-4 border-t border-white/15 pt-3 lg:mt-6 lg:block lg:pt-5 ${listOpen ? 'block' : 'hidden'}`}
        >
          {categories.map((name) => {
            const active = category === name
            return (
              <button
                key={name}
                type="button"
                aria-pressed={active}
                onClick={() => selectCategory(active ? null : name)}
                className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1 text-left text-[13px] font-medium transition-colors ${
                  active ? 'bg-white text-[#0a2b5c]' : 'text-blue-100/85 hover:bg-white/10'
                }`}
              >
                <span className={`h-1 w-1 shrink-0 rounded-full ${active ? 'bg-[#13479c]' : 'bg-sky-300'}`} />
                {name}
              </button>
            )
          })}
        </nav>

        {category && (
          <button
            type="button"
            onClick={() => selectCategory(null)}
            className="mt-5 text-[12.5px] font-semibold text-sky-300 underline underline-offset-4 hover:text-white"
          >
            Show everything
          </button>
        )}
        </div>
        </div>
      </aside>

      {/* ── Body ────────────────────────────────────────────────────── */}
      <div className="flex min-h-0 flex-1 flex-col lg:ml-[400px]">
      <div className="flex-1 px-6 py-10 sm:px-10 lg:px-14 lg:py-14">
        {children}
      </div>
      <section className="px-6 pb-10 sm:px-10 lg:px-14">
        <WhitelabelCTA embedded heading="Get a Quote" buttonText="Get a Quote" />
      </section>
      <Footer className="mt-auto shrink-0 py-6" />
      </div>
    </div>
    </SmartStackContext.Provider>
  )
}

/** Index feed: every post in one column. */
export function SmartStackFeed() {
  const { posts } = useSmartStack()
  if (posts.length === 0) return <Empty />
  return (
    <div>
      {posts.map((p) => (
        <VerdictRow key={p.slug} post={p} />
      ))}
    </div>
  )
}

/** Large row used by the Love / Skip sections — thumb, title, why it made the list. */
function VerdictRow({ post, mute = false }: { post: StackPost; mute?: boolean }) {
  return (
    <Link
      href={post.url}
      className={`group grid grid-cols-[72px_minmax(0,1fr)] gap-6 border-b border-slate-200 py-7 ${
        post.excerpt ? 'items-start' : 'items-center'
      }`}
    >
      <Thumb src={post.image} mute={mute} className="h-16 w-16 rounded-2xl" />
      <div>
        <div
          className={`text-[16px] font-bold leading-[1.25] tracking-[-0.02em] sm:text-[17px] ${
            mute
              ? 'text-slate-500 group-hover:text-slate-700'
              : 'text-[#0a2b5c] group-hover:text-[#13479c]'
          }`}
        >
          {post.title}
        </div>
        {post.excerpt && (
          <p className="mt-2.5 max-w-[64ch] text-[14px] leading-[1.55] text-slate-600">
            {post.excerpt}
          </p>
        )}
      </div>
    </Link>
  )
}

function Empty() {
  return <p className="py-8 text-[14px] text-slate-500">Nothing here in this topic.</p>
}

function SubscribeForm() {
  const [email, setEmail] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle')

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (state === 'sending') return
    setState('sending')
    try {
      const res = await fetch('/api/resources/newsletter-subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'Smart Stack sidebar' }),
      })
      if (res.ok) {
        trackPostHogEvent('form_submitted', { form_source: 'smart-stack-newsletter' })
        setState('done')
      } else {
        setState('error')
      }
    } catch {
      setState('error')
    }
  }

  if (state === 'done') {
    return (
      <p className="mt-6 rounded-lg border border-white/25 bg-white/10 px-3 py-2.5 text-[13px] text-blue-100">
        You&apos;re on the list. Next issue lands Monday.
      </p>
    )
  }

  return (
    <form className="mt-6 flex gap-2" onSubmit={onSubmit}>
      <label className="sr-only" htmlFor="smart-stack-email">
        Email address
      </label>
      <input
        id="smart-stack-email"
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="name@agency.com"
        className="min-w-0 flex-1 rounded-lg border border-white/25 bg-white/10 px-3 py-2.5 text-[13px] text-white placeholder:text-blue-200/80 focus:border-white focus:outline-none"
      />
      <button
        type="submit"
        disabled={state === 'sending'}
        className="shrink-0 rounded-lg bg-white px-4 py-2.5 text-[13px] font-bold text-[#13479c] hover:bg-[#eef4fc] disabled:opacity-70"
      >
        {state === 'sending' ? '…' : 'Subscribe'}
      </button>
      {state === 'error' && (
        <p className="sr-only" role="alert">
          Subscription failed, please try again.
        </p>
      )}
    </form>
  )
}
